import { gql } from '@apollo/client'
import { CustomFieldType } from '@vend/react-utilities'
import uuid from '@vend/vend-uuid'
import angular, { IPromise } from 'angular'
import each from 'lodash/each'
import keyBy from 'lodash/keyBy'
import map from 'lodash/map'
import merge from 'lodash/merge'
import omit from 'lodash/omit'
import sortBy from 'lodash/sortBy'
import md5 from 'md5'
import moment from 'moment-timezone'
import VendNumber, {
  add,
  divide,
  multiply,
  round,
  subtract,
  vn,
} from 'vend-number'

import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import {
  ApiCustomer,
  ApiLineItem,
  ApiLineItemTaxComponent,
  ApiProduct,
  ApiSale,
  EcomCustomCharge,
} from '@vend/types'
import { trackError } from '@vend/utilities'
import { VdFeatureManager, VdTracking } from '@vend/vend.js'
import {
  GqlFulfilmentDetail,
  GqlFulfilmentType,
  GqlLineItem,
  GqlLineItemPromotion,
  GqlLineItemState,
} from '../../scripts/queries/sale_create'
import { ApiSaleAdjustment, SaleAdjustment } from '../../types/adjustments'
import { client } from '../apollo'
import { getProduct } from '../components/product/products-resource/products-resource'
import {
  PROMOTIONS,
  PROMO_CODE_STATUES,
} from '../components/promotions/promotions.constant'
import {
  PromotionsPayload,
  PromotionsPayloadLineItem,
} from '../components/sale/sale-promotion/sale-promotion-resource'
import {
  FEATURE_CAPTURE_ADDRESS_SELL_SCREEN,
  FEATURE_ROUND_TO_2DP_BEFORE_SENDING,
} from '../constants/wr-features'
import { displayRound } from '../filters/display-round'
import { isGiftCard } from '../functions/lineItem'
import { hasCashDiscountingServiceFee, hasChange } from '../functions/payments'
import {
  apiUserToSaleUser,
  determineDisplayStatus,
  getCashDiscountingAdjustments,
  getCashDiscountingAdjustmentsForMutation,
  getFormattedReceiptNumberDetails,
  getTipAdjustments,
  getTipAdjustmentsForMutation,
  hasDiscount,
  hasEcomCustomCharges,
  hasOutstandingBalance,
  isAccountBalancePaymentPaid,
  isConfirmed,
  isFirstTimeSubmitEarly,
  isLegacyServiceSale,
  paymentsHasTip,
} from '../functions/sale'
import {
  formatCustomFieldForMutation,
  formatToClientApi,
  getItemFulfilmentMap,
} from '../functions/sale-transformer'
import {
  hasServiceOrder,
  hasServiceOrderWithAgenda,
  isServiceStatusFinal,
} from '../functions/service'
import { PosStateManager } from '../managers/pos-state-manager'
import { RegisterManager } from '../managers/register-manager'
import { RetailerSettingsManager } from '../managers/retailer-settings-manager'
import { WebRegisterStatusManager } from '../managers/web-register-status-manager'
import {
  CUSTOM_FIELD_VALUE_TYPES,
  CustomFieldValue,
} from '../models/custom-fields'
import { FulfilmentDetail, FulfilmentType } from '../models/fulfilment'
import {
  LineItem,
  LineItemJson,
  LineItemUser,
  createLineItem,
} from '../models/line-item'
import { Payment, PaymentJson } from '../models/payment'
import { Promotion } from '../models/promotion'
import {
  Sale,
  SaleInput,
  SaleJson,
  SalePicklist,
  SaleSource,
} from '../models/sale'
import { SaleDiscount, createSaleDiscount } from '../models/sale-discount'
import {
  SERVICE_ORDERS_CUSTOM_FIELD,
  SaleFulfilmentState,
  SalePaymentState,
  SaleState,
  SaleStatus,
  SaleType,
  UNFULFILLED_STATUSES,
  User,
} from '../models/sale.constants'
import { $log } from '../ng-compat/imports'
import {
  QuoteData,
  QuoteDataWithExtras,
  QuoteStatus,
  TaxComponent,
} from '../queries/quotes'
import {
  GqlSaleAddress,
  SaleAddressMutationData,
} from '../queries/sale-address_create'
import { SaleUpdateActionInput } from '../queries/sale-update'
import { GqlPayment, SaleMutationData } from '../queries/sale_create'
import {
  BasicPriceInfo,
  calculateCustomTaxRates,
} from '../sale-calculation/calculate-line-item'
import {
  calculatePriceComponents,
  calculateTaxAppropriatePrice,
} from '../sale-calculation/calculate-price'
import { calculateTotalTaxRate } from '../sale-calculation/calculate-tax'
import { ReduxCurrentSale } from '../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../util/feature-cache'
import { getStateCode } from '../components/sale/sale-line-item/fulfilment/address/address-validation'
import { CustomersStore } from './database/customers-store'
import { paymentTypesStore } from './database/payment-types-store'
import { ProductsStore } from './database/products-store'
import { TaxesStore } from './database/taxes-store'
import { UsersStore } from './database/users-store'
import {
  GraphQLCustomField,
  GraphQLCustomFieldValue,
  GraphQLPartialFulfillmentAddress,
  GraphQLPicklist,
  GraphQLSale,
} from './gql-types'

export interface ClientApiSaleData {
  id: string
  client_id: string
  client_sale_id: string
  status?: string
  customer_id?: string
  totals: {
    total_tax: string | number
    total_price: string | number
    total_paid: string
    // @deprecated
    total_to_pay: string | number
  }
  discount?: {
    product_id: string
    price: string
    tax: string
    tax_id: string
  }
  line_items: Array<{
    id: string
    product_id: string
    tags: string[]
    gift_card_number?: string
    name: string
    quantity: string | number
    sku: string
    unit_price: string
    unit_tax: string
    tax_id: string | null
    tax_rate: VendNumber | null
    image_url: string
    discount: string
  }>
}

interface NewSaleStatus {
  type: SaleType
  state: SaleState
  paymentState: SalePaymentState
  fulfilmentState: SaleFulfilmentState | null
}

interface EcomCustomCharges {
  name: string
  total: number
  source_id: string
  tax_components: EcomCustomChargeTaxComponent[]
}

interface EcomCustomChargeTaxComponent {
  total_tax: number
  rate_id: string
}

export const SaleQuery = gql`
  fragment CustomFieldValueFragment on CustomFieldValue {
    name
    ... on IntegerCustomFieldValue {
      integerValue
    }
    ... on StringCustomFieldValue {
      stringValue
    }
    ... on BooleanCustomFieldValue {
      booleanValue
    }
    ... on DateCustomFieldValue {
      dateValue
    }
    ... on ProductCustomFieldValue {
      product {
        id
      }
    }
    ... on StringArrayCustomFieldValue {
      stringArrayValue
    }
  }

  fragment CustomFieldFragment on CustomField {
    id
    name
    type
    title
    visibleInUI
    printOnReceipt
  }

  query SaleQuery($id: ID!) {
    sale(id: $id) {
      id
      customFields {
        ...CustomFieldValueFragment
      }
      lineItems {
        id
        customFields {
          ...CustomFieldValueFragment
        }
      }
      picklists {
        id
        type
        note
        lineItems {
          saleLineItem {
            id
          }
        }
      }
      fulfillments {
        status
        note
        lineItems {
          saleLineItem {
            id
          }
        }
      }
    }
    saleCustomFields: customFields(entity: SALE) {
      ...CustomFieldFragment
    }
    lineItemCustomFields: customFields(entity: LINE_ITEM) {
      ...CustomFieldFragment
    }
  }
`

export const PicklistQuery = gql`
  fragment AddressFragment on FulfillmentAddress {
    id
    addressLine1
    addressLine2
    suburb
    city
    postcode
    stateCode
    countryCode
  }

  query PicklistQuery($id: ID!) {
    picklist(id: $id) {
      id
      type
      note
      billingAddress {
        ...AddressFragment
      }
      shippingAddress {
        ...AddressFragment
      }
    }
  }
`

export type PromotionPayloadLineItemParams = BasicPriceInfo &
  Pick<LineItem, 'localId' | 'product' | 'quantity' | 'adjusted' | 'confirmed'>

const CustomFieldTypeFromGraphQL: Record<
  CustomFieldType,
  CUSTOM_FIELD_VALUE_TYPES
> = {
  [CustomFieldType.STRING]: 'string',
  [CustomFieldType.INTEGER]: 'integer',
  [CustomFieldType.BOOLEAN]: 'boolean',
  [CustomFieldType.DATE]: 'date',
  [CustomFieldType.PRODUCT]: 'product_id',
  [CustomFieldType.QRCODE]: 'qrcode',
  [CustomFieldType.STRING_ARRAY]: 'string_array',
  [CustomFieldType.LINK]: 'link',
}

export type ToClientApiSaleParams = Pick<
  Sale | ReduxCurrentSale,
  'id' | 'localId' | 'status' | 'finals' | 'payments' | 'customer' | 'discount'
>

/**
 * Service for transforming between the various representations of a sale (API, model, model JSON).
 */
export class SaleTransformer {
  constructor(
    private readonly retailerSettingsManager: RetailerSettingsManager,
    private readonly registerManager: RegisterManager,
    private readonly vdTracking: VdTracking,
    private readonly productsStore: ProductsStore,
    private readonly taxesStore: TaxesStore,
    private readonly usersStore: UsersStore,
    private readonly saleVersioningManager: any,
    private readonly webRegisterStatusManager: WebRegisterStatusManager,
    private readonly vdFeatureManager: VdFeatureManager,
    private readonly posStateManager: PosStateManager,
    private readonly customersStore: CustomersStore
  ) {
    'ngInject'
  }

  /**
   * Format the given sale as the /api/register_sales end point expects it.
   *
   * @param  sale {Sale}
   *         The sale to transform to the 0.x API format
   *
   * @return {Object} JSON representation of the sale formatted as the API expects it
   */
  public toApi(sale: Sale | ReduxCurrentSale, lineItems: LineItem[]) {
    const receiptNumberDetails = sale.receiptNumberDetails
    let invoiceSequence
    const roundTo2DpFF = getFeatureFromCache(
      FEATURE_ROUND_TO_2DP_BEFORE_SENDING
    )
    if (receiptNumberDetails && typeof receiptNumberDetails !== 'string') {
      invoiceSequence = receiptNumberDetails.number
    }

    const formattedSale = {
      id: sale.id || sale.localId,
      short_code: sale.shortCode,
      sale_date: sale.saleDate,
      status: sale.status,

      customer_id: sale.customer && sale.customer.id,
      register_id: sale.registerId,
      user_id: sale.user && sale.user.id,

      // The API requires the invoice number (a string including the prefix & suffix) and optionally also the
      // invoice sequence (a number)
      invoice_number: getFormattedReceiptNumberDetails(sale),
      invoice_sequence: invoiceSequence,
      resolve_invoice_number: undefined as boolean | undefined,
      total_tax: roundTo2DpFF ? displayRound(sale.finals.tax) : sale.finals.tax,
      total_price: roundTo2DpFF
        ? displayRound(sale.finals.exclusivePrice)
        : sale.finals.exclusivePrice,

      note: sale.note,

      receipt_address: sale.receiptEmailAddress,
      receipt_id: sale.receiptID,

      register_sale_products: lineItems.map((lineItem, i) =>
        this.formatLineItemForApi(lineItem, i)
      ),

      register_sale_payments: map(sale.payments, payment => {
        // Reduce the amount for Account Balance Payment
        if (
          isAccountBalancePaymentPaid(sale.payAccountBalance) &&
          sale.payAccountBalance?.paymentId === payment.localId
        ) {
          return this.formatPaymentForApi({
            ...payment,
            amount: subtract(payment.amount, sale.payAccountBalance.amount),
          })
        }
        return this.formatPaymentForApi(payment)
      }),

      adjustments: [] as any[],

      custom_fields: sale.customFields.map(customField =>
        this.formatCustomFieldForApi(customField)
      ),

      pickable: sale.pickable,

      fulfillment_outlet_id: sale.fulfillmentOutletID,
      service_fields: {},
      metadata: sale.metadata,
    }

    if (this.isSourcedFromQuote(sale)) {
      Object.assign(formattedSale, {
        source: sale.source,
        source_id: sale.sourceId,
      })
    }

    // Clean up the receipt info for the first try of early submission sales,
    // to make sure only email receipt once clicking "Complete Sale"
    if (isFirstTimeSubmitEarly(sale)) {
      formattedSale.receipt_id = ''
      formattedSale.receipt_address = ''
    }

    const saleHasCashDiscountingServiceFee = hasCashDiscountingServiceFee(
      this.registerManager.hasCashDiscounting(),
      sale
    )
    if (saleHasCashDiscountingServiceFee) {
      formattedSale.adjustments.push(
        ...getCashDiscountingAdjustments(
          sale,
          lineItems,
          this.registerManager.getCashDiscountingRate()
        )
      )
    }

    if (paymentsHasTip(sale)) {
      formattedSale.adjustments.push(...getTipAdjustments(sale))
    }

    if (hasChange(sale.change) && sale.change) {
      formattedSale.register_sale_payments.push(
        this.formatPaymentForApi(sale.change)
      )
    }

    if (hasDiscount(sale.discount)) {
      formattedSale.register_sale_products.push(
        this.createDiscountLineItem(sale, lineItems)
      )
    }

    if (sale instanceof Sale && hasEcomCustomCharges(sale)) {
      const ecomcustomCharges: EcomCustomCharges[] = []
      sale.ecomCustomCharges?.charges.map(charge =>
        ecomcustomCharges.push({
          name: charge.name,
          total: charge.total,
          source_id: charge.sourceId,
          tax_components: [],
        })
      )
      Object.assign(formattedSale, {
        ecom_custom_charges: ecomcustomCharges,
      })
    }

    // rules is sale must be submit early, first sync, webregister is online and feature flag is on
    if (
      isFirstTimeSubmitEarly(sale) &&
      this.webRegisterStatusManager.isOnline()
    ) {
      formattedSale.resolve_invoice_number = true
    }

    const saleHasServiceOrder = hasServiceOrder(
      sale.serviceFields,
      sale.customFields
    )

    // For sales with service orders
    if (saleHasServiceOrder) {
      const isSaleStatusClosed = sale.status === SaleStatus.CLOSED
      const saleLevelDiscount = this.createServiceSaleDiscount(sale)

      // Because the register sale API is used to both create and update sales,
      // at intake the service status is going to be 'New'. Continuing a sale with service
      // requires the service to be complete, making its status 'Closed' (or part of the isServiceStatusFinal() function)

      // For new sales with services
      if (!isServiceStatusFinal(sale.serviceFields)) {
        // 1) `register_sale_products` moved to `service_fields.service_line_items`
        Object.assign(formattedSale, {
          service_fields: {
            ...formattedSale.service_fields,
            // the line items also receive the discounts
            service_line_items: formattedSale.register_sale_products,
          },
          register_sale_products: [],
        })

        // 2) Fully paid sales with services will have status 'Service, pending'
        if (isSaleStatusClosed) {
          Object.assign(formattedSale, {
            status: SaleStatus.SERVICE,
          })
        }
      }

      // When continuing sales with complete services
      else {
        // Fully paid sales with services ('Service, pending') must close after receiving full payment
        if (!hasOutstandingBalance(sale) && isLegacyServiceSale(sale.status)) {
          // web and iOS registers have this logic to set the sale status to `CLOSED`,
          // however, because of https://lightspeedhq.atlassian.net/browse/LRX-24285 and
          // the work with the sale states reducer, this changes again to `SERVICE_CLOSED` behind the scenes,
          // making it the final, closed status for fully paid sales with services
          Object.assign(formattedSale, {
            status: SaleStatus.SERVICE_CLOSED,
          })
        }
      }

      // 3) If we have sale level discount, store the percentage & value for services.
      if (saleLevelDiscount) {
        Object.assign(formattedSale, {
          service_fields: {
            ...formattedSale.service_fields,
            sale_level_discount: {
              is_percentage: saleLevelDiscount.isPercentage,
              value: saleLevelDiscount.value,
            },
          },
        })
      }

      // 4) Add agenda to the service
      if (hasServiceOrderWithAgenda(sale)) {
        Object.assign(formattedSale, {
          service_fields: {
            ...formattedSale.service_fields,
            service_agenda: {
              // hasServiceOrderWithAgenda() checks if sale.serviceFields.serviceAgenda exists
              date: sale.serviceFields.serviceAgenda!.date,
              minutes_scheduled: sale.serviceFields.serviceAgenda!
                .minutesScheduled,
            },
          },
          outlet_id: this.posStateManager.getOutletId(),
        })
      }

      // 5) Add customer item to the service
      if (this.hasServiceOrderWithItemDetails(sale)) {
        Object.assign(formattedSale, {
          service_fields: {
            ...formattedSale.service_fields,
            job_item: {
              item: {
                item_id: sale.serviceFields?.serviceItem?.itemDetails?.itemId,
                item_name:
                  sale.serviceFields?.serviceItem?.itemDetails?.itemName,
                serial_number:
                  sale.serviceFields?.serviceItem?.itemDetails?.serialNumber,
                description:
                  sale.serviceFields?.serviceItem?.itemDetails?.description,
                initial_condition:
                  sale.serviceFields?.serviceItem?.itemDetails
                    ?.initialCondition,
              },
              location: sale.serviceFields?.serviceItem?.location,
            },
          },
        })
      }

      // 6) Apply proper action like 'CREATE_JOB', 'UPDATE_JOB'
      Object.assign(formattedSale, {
        service_fields: {
          ...formattedSale.service_fields,
          service_action: sale.serviceFields?.serviceAction,
        },
      })
    }

    return formattedSale
  }

  /**
   * Format the given AddressDetails to saleAddress
   *
   * @param address {AddressDetails} - the address to transform to the saleAddress format
   *
   * @returns GqlSaleAddress - the transformed address
   */
  private formatAddressToSaleAddress(
    address?: AddressDetails
  ): GqlSaleAddress | undefined {
    if (!address) return undefined

    return {
      addressLine1: address.address_1,
      addressLine2: address.address_2 || '',
      city: address.city,
      state: getStateCode(address.state, address.country_id),
      postalCode: address.postcode,
      countryCode: address.country_id,
      suburb: address.suburb,
    }
  }

  public toSaleAddressMutation(
    fulfillmentDetails: FulfilmentDetail[],
    customerId?: string
  ): SaleAddressMutationData {
    const { billingAddress, shippingAddress } = fulfillmentDetails[0]
    const formattedAddresses: SaleAddressMutationData = {
      billingAddress: this.formatAddressToSaleAddress(billingAddress)!,
      shippingAddress: this.formatAddressToSaleAddress(shippingAddress)!,
      customerId: customerId || '',
    }

    return formattedAddresses
  }

  /**
   * Format the given sale as the CreateSale mutation gql expects it.
   *
   * @param sale {Sale} - the sale to transform to the mutation format
   *
   * @returns SaleMutationData - the transformed sale
   */
  public toSaleMutation(
    sale: Sale | ReduxCurrentSale,
    lineItems: LineItem[]
  ): SaleMutationData {
    const receiptNumberDetails = sale.receiptNumberDetails
    let invoiceSequence

    if (receiptNumberDetails && typeof receiptNumberDetails !== 'string') {
      invoiceSequence = receiptNumberDetails.number
    }

    const user = sale.user || this.posStateManager.getUser()

    const saleMutation: SaleMutationData = {
      id: sale.id || sale.localId,
      // The API requires the invoice number (a string including the prefix & suffix) and optionally also the invoice sequence (a number)
      invoiceNumber: getFormattedReceiptNumberDetails(sale),
      // Use md5 hash function to make sure to generate same hash value with the same sale input,
      // so when sale has no change, will always get the same result. That describes the pair concept better
      mutationID:
        sale.mutationID ??
        md5(
          JSON.stringify({
            ...sale,
            syncStatus: undefined,
            syncAttempts: undefined,
          })
        ),
      registerID: sale.registerId!,
      legacyStatus: sale.status!,
      userID: user?.id || '',
      receipt: {
        id: sale.receiptID,
        recipientEmail: sale.receiptEmailAddress,
      },
      note: sale.note,
      saleDate: sale.saleDate,
      shortCode: sale.shortCode,
      source: sale.source,
      sourceID: sale.sourceId,
      invoiceSequence,
      payments: sale.payments.map(payment => {
        // Reduce the amount for Account Balance Payment
        if (
          isAccountBalancePaymentPaid(sale.payAccountBalance) &&
          sale.payAccountBalance?.paymentId === payment.localId
        ) {
          return this.formatPaymentForMutation({
            ...payment,
            amount: subtract(payment.amount, sale.payAccountBalance.amount),
          })
        }
        return this.formatPaymentForMutation(payment)
      }),
      lineItems: lineItems.map((lineItem, i) =>
        this.formatLineItemForMutation(lineItem, i)
      ),
      adjustments: [],
      customFields: sale.customFields.map(customField =>
        formatCustomFieldForMutation(customField)
      ),
      pickable: sale.pickable,
      fulfilmentDetails: sale.fulfilmentDetails
        .filter(
          fulfilmentDetail =>
            fulfilmentDetail.type !== FulfilmentType.UNSPECIFIED
        )
        .map(
          fulfilmentDetail =>
            ({
              type: fulfilmentDetail.type.toString(),
              note: fulfilmentDetail.note,
            } as GqlFulfilmentDetail)
        ),
      metadata: sale.metadata,
    }

    if (sale.customer) {
      saleMutation.customerID = sale.customer.id
    }

    if (
      isFirstTimeSubmitEarly(sale) &&
      this.webRegisterStatusManager.isOnline()
    ) {
      saleMutation.resolveInvoiceNumber = true
    }

    if (this.isSourcedFromQuote(sale)) {
      saleMutation.source = sale.source
      saleMutation.sourceID = sale.sourceId
    }

    // Clean up the receipt info for the first try of early submission sales,
    // to make sure only email receipt once clicking "Complete Sale"
    if (isFirstTimeSubmitEarly(sale)) {
      saleMutation.receipt = {
        id: '',
        recipientEmail: '',
      }
    }

    const hasCashDiscounting = this.registerManager.hasCashDiscounting()
    if (hasCashDiscountingServiceFee(hasCashDiscounting, sale)) {
      saleMutation.adjustments.push(
        ...getCashDiscountingAdjustmentsForMutation(
          sale,
          lineItems,
          this.registerManager.getCashDiscountingRate()
        )
      )
    }

    if (paymentsHasTip(sale)) {
      saleMutation.adjustments.push(...getTipAdjustmentsForMutation(sale))
    }

    if (hasChange(sale.change) && sale.change) {
      saleMutation.payments.push(this.formatPaymentForMutation(sale.change))
    }

    if (hasDiscount(sale.discount)) {
      saleMutation.lineItems.push(
        this.createDiscountLineItemForMutation(sale, lineItems)
      )
    }

    const saleHasServiceOrder = hasServiceOrder(
      sale.serviceFields,
      sale.customFields
    )

    // For sales with service orders
    if (saleHasServiceOrder) {
      const isSaleStatusClosed = sale.status === SaleStatus.CLOSED
      const saleLevelDiscount = this.createServiceSaleDiscount(sale)

      // 1) `sale.lineItems` move to `sale.serviceFields.serviceLineItems`
      saleMutation.serviceFields = {
        // the line items also receive the discounts
        serviceLineItems: saleMutation.lineItems,
      }
      saleMutation.lineItems = []

      // 2) Fully paid sales with services will have status 'Service, pending'
      if (isSaleStatusClosed) {
        saleMutation.legacyStatus = SaleStatus.SERVICE
      }

      // 3) If we have sale level discount, store the percentage & value
      if (saleLevelDiscount) {
        saleMutation.serviceFields = {
          ...saleMutation.serviceFields,
          saleLevelDiscount,
        }
      }

      // 4) Add agenda to the service
      if (hasServiceOrderWithAgenda(sale)) {
        saleMutation.serviceFields = {
          ...saleMutation.serviceFields,
          serviceAgenda: sale.serviceFields.serviceAgenda,
        }
      }

      // 5) Add customer item to the service
      if (this.hasServiceOrderWithItemDetails(sale)) {
        saleMutation.serviceFields = {
          ...saleMutation.serviceFields,
          serviceItem: sale.serviceFields.serviceItem!,
        }
      }

      // 6) Apply proper action like 'CREATE_JOB', 'UPDATE_JOB'
      saleMutation.serviceFields = {
        ...saleMutation.serviceFields,
        serviceAction: sale.serviceFields.serviceAction,
      }
    }

    return saleMutation
  }

  /**
   * Build the payload to send to the /api/2.0/discount endpoint based on the given sale information.
   */
  // TODO: Is there a more appropriate place for this to live? Especially once the deprecated toPromotion above is
  // cleaned up.
  public toPromotionsPayload(
    promoCode: string,
    customer: ApiCustomer | null,
    lineItemsParams: PromotionPayloadLineItemParams[]
  ): PromotionsPayload {
    const activeOutlet = this.registerManager.getActiveOutlet()
    const customerGroupId =
      (customer?.customer_code !== 'WALKIN' && customer?.customer_group_id) ||
      null

    return {
      channel: 'Register',
      promo_code: promoCode || '',
      customer: {
        customer_group_id: customerGroupId,
      },
      is_loyalty_enabled: this.retailerSettingsManager.isLoyaltyEnabled(),
      is_tax_inclusive: this.registerManager.isTaxInclusive(),
      outlet_id: activeOutlet.id,
      sale: {
        sale_date: moment
          .tz(activeOutlet.time_zone)
          .format(PROMOTIONS.DATE_FORMAT),
        line_items: lineItemsParams.map(lineItemParams =>
          this.toPromotionsPayloadLineItem(lineItemParams)
        ),
      },
    }
  }

  /**
   * Format and hydrate the given sale representation to a full Sale model. Inspects the given representation to
   * determine what format to transform from (currently supports Sale model JSON and 2.0 sales API).
   *
   * @note MODIFICATION: the given sale JSON will likely be modified in the process of transformation. At the time
   * of writing this method this was not an issue for the only known use cases and so defensively cloning the
   * given JSON was not worth the performance hit.
   *
   * @param  {Object} sale
   *         The representation of a sale to convert to a Sale model. @note This object will likely be modified in
   *         the process.
   *
   * @return {Sale} The full Sale model representation of the given sale data
   */
  public toModel(sale: ApiSale | Sale | SaleJson): IPromise<Sale> {
    return new Promise((resolve, reject) => {
      if (!sale || sale instanceof Sale) {
        // Already an instance of the Sale model (or null/undefined) so return as is
        resolve(sale)
      } else {
        if (Array.isArray((sale as any).lineItems)) {
          // JSON representation of the Sale model so ensure its in the latest model version
          const updatedSaleJson = this.saleVersioningManager.convertToLatestModelVersion(
            sale
          )
          return this.hydrateSale(updatedSaleJson).then(resolve, reject)
        } else {
          // Must be an API representation of the sale
          return this.formatSaleJsonForModel(sale as ApiSale)
            .then(saleJson => {
              return this.hydrateSale(saleJson)
            })
            .then(resolve, reject)
        }
      }
    })
  }

  /**
   * Format the given sale to a format suitable to be passed to integrations via the Client API.
   *
   * @method toClientApi
   *
   * @param  sale {Sale}
   *         The sale to transform to the Client API format
   * @deprecated Please use `sale-transformer` in script/functions instead
   * @return {Object} JSON representation of the sale formatted as the Client API expects it
   */
  public toClientApi(
    // TODO: Remove Readonly<Sale> when upstreams have been migrated to redux
    sale: Readonly<ToClientApiSaleParams>,
    lineItems: ReadonlyArray<Readonly<LineItem>>
  ): ClientApiSaleData {
    return formatToClientApi(
      sale,
      lineItems,
      this.retailerSettingsManager.get().discount_product_id
    )
  }

  /**
   * Format the the input for the update sale mutation
   * @returns { SaleUpdateActionInput  }
   */
  public formatUpdateSaleInput(
    sale: Readonly<ReduxCurrentSale> | Readonly<Sale>,
    hasAddedCustomer: boolean,
    hasUpdatedSaleNote: boolean
  ) {
    const updatedSale: SaleUpdateActionInput = {
      saleID: sale.id || sale.localId,
    }

    if (hasAddedCustomer && sale.customer) {
      updatedSale.saleUpdateCustomer = { customerID: sale.customer.id }
    }

    if (hasUpdatedSaleNote) {
      updatedSale.saleUpdateNote = { note: sale.note }
    }
    return updatedSale
  }

  /**
   * Returns full product data for a provided product id. If the product fails to be retrieved from the local store,
   * a request is made to the API (to handle deleted products).
   *
   * @param {String} productId
   *        The id of the product to retrieve
   *
   * @returns {Promise<Product>} The requested product.
   */
  public hydrateProduct(productId: string): IPromise<ApiProduct | undefined> {
    return new Promise(resolve => {
      if (!productId) {
        return resolve(undefined)
      }

      this.productsStore.get(productId).then(product => {
        if (product) {
          return resolve(product)
        }

        this.vdTracking.trackUserEvent({
          eventName: 'Retrieving Deleted Product',
          metadata: {
            productId,
          },
        })

        // Product not found (most likely deleted) so retrieve it from API
        getProduct(productId)
          .then(product => {
            if (!product) {
              return Promise.reject(
                new Error('Product was not returned from the API')
              )
            }

            return this.productsStore.put(product).then(() => resolve(product))
          })
          .catch(error => {
            $log.error(
              '[SaleTransformer] Failed to hydrate a product via the API',
              {
                data: {
                  error,
                  productId,
                },
              }
            )
            resolve(undefined)
          })
      })
    })
  }

  // ============================== Private API ============================== //

  /**
   * Helper to track the cause of lineItems missing product ids
   *
   * @param {LineItem} lineItem
   *        The line item to retrieve the product id from
   *
   * @returns {String} The product id for the provided lineItem
   */
  private getLineItemProductId(lineItem: LineItem): string {
    const productId = lineItem.product.id

    if (!productId) {
      trackError('[Sale Transformer] Failed to get product id from line item', {
        data: {
          stack: new Error().stack,
        },
      })
    }

    return productId
  }

  private toPromotionsPayloadLineItem({
    localId,
    product,
    quantity,
    adjusted,
    confirmed,
    ...pricing
  }: PromotionPayloadLineItemParams): PromotionsPayloadLineItem {
    return {
      id: localId,
      handle: product.handle,
      product_id: product.id,
      product_brand_id: product.brand_id,
      product_supplier_id: product.supplier_id,
      product_type_id: product.product_type_id,
      product_category: product.product_category,
      product_tag_ids: product.tag_ids,
      quantity: vn(quantity).toNumber(),
      unit_price: pricing.price,
      unit_tax: pricing.tax,
      price_adjusted: adjusted,
      confirmed,
      loyalty_value: pricing.loyaltyValue,
      explicit_loyalty_value: pricing.explicitLoyaltyValue,
      variant_parent_id: product.variant_parent_id,
    }
  }

  /**
   * Create the discount line item containing all the info in the SaleDiscount model to send to the server.
   */
  private createDiscountLineItem(
    sale: Sale | ReduxCurrentSale,
    lineItems: LineItem[]
  ) {
    const discount = sale.discount!
    let saleDiscountTaxComponents = []
    const saleDiscountTaxComponentsMap: {
      [key: string]: any
    } = {}
    lineItems.forEach(lineItem => {
      if (!vn(lineItem.totals.saleDiscountTax).eq(0)) {
        // create the map element if necessary
        lineItem.taxComponents.forEach(taxComponent => {
          saleDiscountTaxComponentsMap[
            taxComponent.rate_id
          ] = saleDiscountTaxComponentsMap[taxComponent.rate_id] || {
            total_tax: 0,
            rate_id: taxComponent.rate_id,
          }

          // add any previous intermediate result
          saleDiscountTaxComponentsMap[taxComponent.rate_id].total_tax = add(
            saleDiscountTaxComponentsMap[taxComponent.rate_id].total_tax,
            subtract(
              taxComponent.total_tax,
              (taxComponent as any).total_tax_after_sale_discount
            )
          )
        })
      }
    })

    saleDiscountTaxComponents = Object.keys(saleDiscountTaxComponentsMap).map(
      rateId => ({
        rate_id: rateId,
        total_tax: -saleDiscountTaxComponentsMap[rateId].total_tax,
      })
    )
    return {
      id: discount.id || discount.localId,

      product_id: this.retailerSettingsManager.get().discount_product_id,
      price_set: 1,

      price: discount.price,
      tax: discount.tax,
      tax_id: discount.taxInfo!.id,
      discount: 0,
      loyalty_value: 0,
      tax_components: saleDiscountTaxComponents.length
        ? saleDiscountTaxComponents
        : undefined,

      // Confirmed for retrieved LAYBY or ONACCOUNT items, otherwise SAVED
      status:
        isConfirmed(sale) || sale.isSavedUnfulfilled ? 'CONFIRMED' : 'SAVED',

      quantity: -1,
      sequence: lineItems.length,
      fulfilment_type: discount.fulfilmentType,
    }
  }

  /**
   * Creates a discount line item compatible with the graphql mutation
   *
   * @param sale
   *
   * @returns GqlLineItem
   */
  private createDiscountLineItemForMutation(
    sale: Sale | ReduxCurrentSale,
    lineItems: LineItem[]
  ): GqlLineItem {
    const discount = sale.discount!
    let saleDiscountTaxComponents: Array<{ rateID: string; total: string }> = []
    const saleDiscountTaxComponentsMap: {
      [key: string]: any
    } = {}

    lineItems.forEach(lineItem => {
      if (!vn(lineItem.totals.saleDiscountTax).eq(0)) {
        // create the map element if necessary
        lineItem.taxComponents.forEach(taxComponent => {
          saleDiscountTaxComponentsMap[
            taxComponent.rate_id
          ] = saleDiscountTaxComponentsMap[taxComponent.rate_id] || {
            total_tax: 0,
            rate_id: taxComponent.rate_id,
          }

          // add any previous intermediate result
          saleDiscountTaxComponentsMap[taxComponent.rate_id].total_tax = add(
            saleDiscountTaxComponentsMap[taxComponent.rate_id].total_tax,
            subtract(
              taxComponent.total_tax,
              (taxComponent as any).total_tax_after_sale_discount
            )
          )
        })
      }
    })

    saleDiscountTaxComponents = Object.keys(saleDiscountTaxComponentsMap).map(
      rateId => ({
        rateID: rateId,
        total: `${-saleDiscountTaxComponentsMap[rateId].total_tax}`,
      })
    )

    // RegisterId and RegisterSequenceId are not properly set for Quotes converted to Sales.
    if (discount.registerId === undefined) {
      discount.registerId = this.registerManager.getActiveRegister().id
      discount.registerSequenceId = this.registerManager.getActiveRegister().register_open_sequence_id!
    }

    const discountLineItem: GqlLineItem = {
      id: discount.id || discount.localId,

      productID: this.retailerSettingsManager.get().discount_product_id,
      priceSet: true,

      registerID: discount.registerId,
      registerSequenceID: discount.registerSequenceId,

      unitPrice: discount.price.toString(),
      unitDiscount: '0',
      unitLoyalty: '0',

      // Confirmed for retrieved LAYBY or ONACCOUNT items, otherwise SAVED
      state:
        isConfirmed(sale) || sale.isSavedUnfulfilled
          ? GqlLineItemState.CONFIRMED
          : GqlLineItemState.RESERVED,

      quantity: '-1',
      sequence: lineItems.length,
      taxComponents: saleDiscountTaxComponents,
      fulfilmentType: (discount.fulfilmentType as unknown) as GqlFulfilmentType,
    }

    return discountLineItem
  }

  /**
   * Format the given line item as the /api/register_sales end point expects it.
   *
   * @param  lineItem {LineItem}
   *         A line item to transform to the 0.x API format
   *
   * @param  index {Number}
   *         The index of the line item within the sale
   *
   * @return {Object} JSON representation of the line item formatted as the API expects it
   */
  private formatLineItemForApi(lineItem: LineItem, index: number): any {
    const attributes = [
      {
        name: 'line_note',
        value: lineItem.note as any,
      },
    ]

    const roundTo2DpFF = getFeatureFromCache(
      FEATURE_ROUND_TO_2DP_BEFORE_SENDING
    )

    if (isGiftCard(lineItem.product)) {
      attributes.push(
        {
          name: 'vend.gift_card.number',
          value: lineItem.giftCardNumber,
        },
        {
          name: 'vend.gift_card.amount',
          value: lineItem.basePrice,
        }
      )

      if (lineItem.giftCardReload) {
        attributes.push({
          name: 'vend.gift_card.is_reload',
          value: lineItem.giftCardReload,
        })
      }

      if (lineItem.giftCardClientId) {
        attributes.push({
          name: 'vend.gift_card.client_id',
          value: lineItem.giftCardClientId,
        })
      }

      if (lineItem.giftCardExpiresAt) {
        attributes.push({
          name: 'vend.gift_card.expires_at',
          value: lineItem.giftCardExpiresAt,
        })
      }
    }

    const tax = vn(lineItem.quantity).isZero()
      ? 0
      : divide(vn(lineItem.totals.tax), vn(lineItem.quantity))

    const apiLineItem = {
      // @todo ID should be server generated - ETA next version of API
      id: lineItem.id || lineItem.localId,

      product_id: this.getLineItemProductId(lineItem),

      // Price is always exclusive
      price: roundTo2DpFF
        ? displayRound(lineItem.price)
        : round(lineItem.price, 5),
      price_set: lineItem.adjusted ? 1 : 0,

      // @note depending on config discount is posted as inclusive or exclusive (i.e. display value)
      // @todo this sucks. The API should accept an exclusive value same as price
      discount: lineItem.displayDiscount,

      tax: roundTo2DpFF ? displayRound(tax) : round(tax, 5),
      tax_id: lineItem.taxInfo!.id,

      loyalty_value: lineItem.loyaltyValue,

      quantity: lineItem.quantity,

      sequence: index,

      // Confirmed for retrieved LAYBY or ONACCOUNT items, otherwise SAVED
      status: lineItem.confirmed ? 'CONFIRMED' : 'SAVED',

      attributes,

      tax_components: lineItem.taxComponents.map(taxComponent => ({
        rate_id: taxComponent.rate_id,
        total_tax: roundTo2DpFF
          ? displayRound(taxComponent.total_tax)
          : taxComponent.total_tax,
      })),

      custom_fields: lineItem.customFields.map(customField =>
        this.formatCustomFieldForApi(customField)
      ),
    }

    if (lineItem.salesperson) {
      ;(apiLineItem as any).salesperson_id = lineItem.salesperson.id
    }

    if (lineItem.promotionDetails) {
      const apiPromotionDetails: unknown[] = []

      lineItem.promotionDetails.forEach(promotionDetail => {
        const apiPromotionDetail = {
          promotion_id: promotionDetail.id,
          name: promotionDetail.name,
          amount: promotionDetail.amount,
          promo_code: promotionDetail.promoCode,
          promo_code_id: promotionDetail.promoCodeId,
        }

        apiPromotionDetails.push(apiPromotionDetail)
      })
      ;(apiLineItem as any).promotions = apiPromotionDetails
    }

    if (
      lineItem.fulfilmentType === FulfilmentType.PICKUP ||
      lineItem.fulfilmentType === FulfilmentType.DELIVERY
    ) {
      ;(apiLineItem as any).fulfillment_type = lineItem.fulfilmentType
    }

    return apiLineItem
  }

  /**
   * Format the given line item as the graphql mutation expects it.
   *
   * @param  lineItem {LineItem}
   *         A line item to transform
   *
   * @param  index {Number}
   *         The index of the line item within the sale
   *
   * @return {GqlLineItem}
   *
   * @todo   This function is not passing the attributes data at the moment
   *         Add it when necessary in future
   */
  private formatLineItemForMutation(
    lineItem: LineItem,
    index: number
  ): GqlLineItem {
    const roundTo2DpFF = getFeatureFromCache(
      FEATURE_ROUND_TO_2DP_BEFORE_SENDING
    )

    const mutationLineItem: GqlLineItem = {
      id: lineItem.id || lineItem.localId,

      quantity: vn(lineItem.quantity).toString(),

      registerID: lineItem.registerId,

      registerSequenceID: lineItem.registerSequenceId,

      priceSet: lineItem.adjusted || false,

      productID: this.getLineItemProductId(lineItem),

      sequence: index,

      sku: lineItem.product.sku,

      state: lineItem.confirmed
        ? GqlLineItemState.CONFIRMED
        : GqlLineItemState.RESERVED,

      taxComponents: lineItem.taxComponents.map(taxComponent => ({
        rateID: taxComponent.rate_id,
        total: roundTo2DpFF
          ? displayRound(taxComponent.total_tax).toString()
          : vn(taxComponent.total_tax).toString(),
      })),

      unitDiscount: vn(lineItem.displayDiscount).toString(),

      unitLoyalty: vn(lineItem.loyaltyValue).toString(),

      unitPrice: roundTo2DpFF
        ? displayRound(lineItem.price).toString()
        : round(lineItem.price, 5),
    }

    if (lineItem.salesperson) {
      mutationLineItem.salespersonID = lineItem.salesperson.id
    }

    if (lineItem.customFields.length > 0) {
      mutationLineItem.customFields = lineItem.customFields.map(customField =>
        formatCustomFieldForMutation(customField)
      )
    }

    if (isGiftCard(lineItem.product)) {
      mutationLineItem.giftCard = {
        number: lineItem.giftCardNumber!,
        amount: lineItem.basePrice,
        clientID: lineItem.giftCardClientId!,
        expiresAt: lineItem.giftCardExpiresAt!,
      }

      if (lineItem.giftCardReload) {
        mutationLineItem.giftCard.isReload = lineItem.giftCardReload
      }
    }

    if (lineItem.note) {
      mutationLineItem.note = lineItem.note
    }

    if (lineItem.promotionDetails) {
      const gqlLineItemPromotions: GqlLineItemPromotion[] = []

      lineItem.promotionDetails.forEach(promotionDetail => {
        const apiPromotionDetail: GqlLineItemPromotion = {
          promotionID: promotionDetail.id!,
          amount: vn(promotionDetail.amount).toString(),
        }

        if (promotionDetail.promoCode) {
          apiPromotionDetail.promoCode = promotionDetail.promoCode
        }

        if (promotionDetail.promoCodeId) {
          apiPromotionDetail.promoCodeID = promotionDetail.promoCodeId
        }

        gqlLineItemPromotions.push(apiPromotionDetail)
      })

      mutationLineItem.promotions = gqlLineItemPromotions
    }

    const statusToFulfilmentType: {
      [key: string]: GqlFulfilmentType
    } = {
      [FulfilmentType.DELIVERY]: GqlFulfilmentType.DISPATCH,
      [FulfilmentType.PICKUP]: GqlFulfilmentType.PICKUP,
    }

    if (lineItem.fulfilmentType) {
      const fulfilmentType = statusToFulfilmentType[lineItem.fulfilmentType]

      mutationLineItem.fulfilmentType = fulfilmentType
    }

    return mutationLineItem
  }

  private formatCustomFieldForApi(customFieldValue: CustomFieldValue) {
    switch (customFieldValue.type) {
      case 'string':
      case 'date':
      case 'product_id':
      case 'qrcode':
        return {
          name: customFieldValue.name,
          string_value: customFieldValue.value,
        }
      case 'integer':
        return {
          name: customFieldValue.name,
          integer_value: customFieldValue.value,
        }
      case 'boolean':
        return {
          name: customFieldValue.name,
          boolean_value: customFieldValue.value,
        }
      case 'string_array':
        return {
          name: customFieldValue.name,
          string_array_value: customFieldValue.value,
        }
    }
  }

  /**
   * Format the given payment as the /api/register_sales end point expects it.
   *
   * @param  payment {Payment}
   *         A payment to transform to the 0.x API format
   *
   * @return {Object} JSON representation of the payment formatted as the API expects it
   */
  private formatPaymentForApi(payment: Payment) {
    const activeOutlet = this.registerManager.getActiveOutlet()

    const response: any = {
      // @todo ID should be server generated - ETA next version of API
      id: payment.id || payment.localId,

      register_id: payment.registerId,
      payment_type_id: payment.paymentType.type_id,
      retailer_payment_type_id: payment.paymentType.id,
      payment_date: payment.paymentDate,
      amount: payment.amount,
      currency: activeOutlet.currency,
      source: payment.source,
      source_id: payment.sourceId,
    }

    if (payment.surchargeData && !vn(payment.surchargeData.amount).eq(0)) {
      response.surcharge = {
        amount: vn(payment.surchargeData.amount).toString(),
        rule: payment.surchargeData.rule,
      }
    }

    const { terminal, card, emv } = payment
    if (
      payment.external_transaction_id ||
      payment.referenceId ||
      payment.merchantId ||
      payment.authCode ||
      payment.signatureURL ||
      payment.signatureBase64 ||
      payment.transactionStatus ||
      terminal ||
      card ||
      emv
    ) {
      response.external_attributes = {
        transaction_id: payment.external_transaction_id,
        reference_id: payment.referenceId,
        merchant_id: payment.merchantId,
        auth_code: payment.authCode,
        signature_url: payment.signatureURL,
        signature_base64: payment.signatureBase64,
        transaction_status: payment.transactionStatus,
      }
      if (terminal) {
        response.external_attributes.terminal = {
          serial_number: terminal.serialNumber,
          model: terminal.model,
        }
      }
      if (card) {
        response.external_attributes.card = {
          entry_method: card.entryMethod,
          brand: card.brand,
          type: card.type,
          last_four: card.lastFour,
          cardholder_name: card.cardholderName,
        }
      }
      if (emv) {
        response.external_attributes.emv = {
          aid: emv.aid,
          application_label: emv.applicationLabel,
          cryptogram_type: emv.cryptogramType,
          cryptogram: emv.cryptogram,
          pin_verified: emv.pinVerified,
          tags: emv.tags,
        }
      }
    }

    return response
  }

  /**
   * Format the given payment as the createSaleMuation gql expects it.
   *
   * @param  payment {Payment}
   *         A payment to transform to the gql format
   *
   * @return {Object} JSON representation of the payment formatted as the mutation gql expects it
   */
  private formatPaymentForMutation(payment: Payment): GqlPayment {
    const activeOutlet = this.registerManager.getActiveOutlet()

    const response: GqlPayment = {
      // @todo ID should be server generated - ETA next version of API
      id: payment.id || payment.localId,

      registerID: payment.registerId,
      registerSequenceID: payment.registerSequenceId,
      retailerPaymentTypeID: payment.paymentType.id,
      paymentTypeID: payment.paymentType.payment_type?.id,
      paymentDate: payment.paymentDate,
      amount: vn(payment.amount).toString(),
      currency: activeOutlet.currency,
      source: payment.source,
      sourceID: payment.sourceId,
    }

    const { terminal, card, emv, surchargeData } = payment

    if (surchargeData && !vn(surchargeData.amount).eq(0)) {
      response.surcharge = {
        amount: vn(surchargeData.amount).toString(),
        rule: surchargeData.rule,
      }
    }

    if (
      payment.external_transaction_id ||
      payment.referenceId ||
      payment.merchantId ||
      payment.authCode ||
      payment.signatureURL ||
      payment.signatureBase64 ||
      payment.transactionStatus ||
      terminal ||
      card ||
      emv
    ) {
      response.externalAttributes = {
        transactionID: payment.external_transaction_id,
        referenceID: payment.referenceId,
        merchantID: payment.merchantId,
        authCode: payment.authCode,
        signatureUrl: payment.signatureURL,
        signatureBase64: payment.signatureBase64,
        transactionStatus: payment.transactionStatus,
      }
      if (terminal) {
        response.externalAttributes.terminal = {
          serialNumber: terminal.serialNumber,
          model: terminal.model,
        }
      }
      if (card) {
        response.externalAttributes.card = {
          entryMethod: card.entryMethod,
          brand: card.brand,
          type: card.type,
          lastFour: card.lastFour,
          cardholderName: card.cardholderName,
        }
      }
      if (emv) {
        response.externalAttributes.emv = {
          aid: emv.aid,
          applicationLabel: emv.applicationLabel,
          cryptogramType: emv.cryptogramType,
          cryptogram: emv.cryptogram,
          pinVerified: emv.pinVerified,
          tags: emv.tags,
        }
      }
    }

    return response
  }

  /**
   * Format the given API JSON representation of a sale to the Sale model JSON format for constructing a Sale
   * model of it.
   *
   * @param  saleApiJson {Object}
   *         JSON representation of a sale as returned by the API
   *
   * @return {Object} JSON representation of the given sale that can be used to construct a Sale model
   */
  private formatSaleJsonForModel(saleApiJson: ApiSale): IPromise<SaleJson> {
    // Any promises tracking async operations
    let promiseQueue = Promise.resolve()
    const discountProductId = this.retailerSettingsManager.get()
      .discount_product_id
    const saleJson: any = {
      id:
        // only set sale.id to undefined when creating a new quote converted sale, which saleApiJson.created_at is empty
        this.isSourcedFromQuote(saleApiJson) && !saleApiJson.created_at
          ? undefined
          : saleApiJson.id,
      localId: (saleApiJson as any).localId || saleApiJson.id,

      lineItems: [],

      note: saleApiJson.note,

      customer_id: saleApiJson.customer_id,
      status: saleApiJson.status,

      returnFor: saleApiJson.return_for,
      shortCode: saleApiJson.short_code,

      displayStatus: determineDisplayStatus(saleApiJson),

      receiptNumberDetails: saleApiJson.invoice_number,

      // For Parked sales, these properties will be regenerated prior to posting to the server (see
      // saleManager._transitionToStatus)
      saleDate: saleApiJson.sale_date,
      registerId: saleApiJson.register_id,
      user_id: saleApiJson.user_id,

      // Handle continuing non-tax sale
      isSavedNonTax: vn(saleApiJson.total_tax).isZero(),

      // Handle continuing not fully paid unfulfilled sale
      isSavedUnfulfilled: UNFULFILLED_STATUSES.includes(
        (saleApiJson.status as unknown) as SaleStatus
      ),
      // Handle continuing not fully paid service sale
      isSavedService:
        ((saleApiJson.status as unknown) as SaleStatus) ===
          SaleStatus.SERVICE ||
        ((saleApiJson as any).attributes || []).includes('service') ||
        ((saleApiJson as any).custom_fields || []).some(
          (field: any) =>
            field.name === SERVICE_ORDERS_CUSTOM_FIELD && field.value
        ),
      // handle lspayecom refund
      sourceId: saleApiJson.source_id,
      // TODO: add type adjustments to type ApiSale after resolve build failure with @vend/types v17.1.0
      adjustments: Array.isArray((saleApiJson as any).adjustments)
        ? (saleApiJson as any).adjustments
        : [],
      attributes: (saleApiJson as any).attributes || [],
    }

    if (this.isSourcedFromQuote(saleApiJson)) {
      Object.assign(saleJson, {
        source: saleApiJson.source,
        sourceId: saleApiJson.source_id,
      })
    }

    const addOrUpdateSaleDiscount = (discountLineItem: ApiLineItem) => {
      let discount = saleJson.discount

      saleJson.discountIsPercentage = false

      if (!discount) {
        discount = {
          id: discountLineItem.id,
          localId: (discountLineItem as any).localId || discountLineItem.id,

          tax_id: discountLineItem.tax_id,

          price: '0',
          tax: '0',
        }
        // Add tax components if they are present, only applicable for 2.0 API.
        // Line item tax components are the total tax amounts grouped by rate id,
        // this is the most correct version of tax data
        if (discountLineItem.tax_components) {
          discount.taxComponents = discountLineItem.tax_components
        }

        saleJson.discount = discount
      }

      const quantity = multiply(discountLineItem.quantity, -1)
      const price = multiply(discountLineItem.price, quantity)
      const tax = multiply(discountLineItem.tax, quantity)

      discount.price = add(discount.price, price)
      discount.tax = add(discount.tax, tax)
    }

    each(sortBy(saleApiJson.line_items, 'sequence'), lineItem => {
      if (lineItem.product_id === discountProductId) {
        addOrUpdateSaleDiscount(lineItem)
      } else {
        // Guarantee _addLineItemToSaleJson executes in order for each line item, ensuring that the
        // correct number of lineItems are added to the saleJson.
        promiseQueue = promiseQueue.then(() =>
          this.addLineItemToSaleJson(lineItem, saleJson)
        )
      }
    })

    if (saleJson.discount) {
      saleJson.discount.price = round(saleJson.discount.price, 5)
      saleJson.discount.tax = round(saleJson.discount.tax, 5)
    }

    saleJson.payments = map(saleApiJson.payments, (paymentApiJson: any) => {
      return {
        id: paymentApiJson.id,
        localId: paymentApiJson.localId || paymentApiJson.id,
        payment_type_id: paymentApiJson.retailer_payment_type_id,
        registerId: paymentApiJson.register_id,
        paymentDate: paymentApiJson.payment_date,
        amount: paymentApiJson.amount,
        // Transactional data currently only used for gift card payments; not currently returned from api/2.0/sales
        source: paymentApiJson.source,
        sourceId: paymentApiJson.source_id,
        surchargeData: isNaN(paymentApiJson?.surcharge?.amount)
          ? undefined
          : {
              amount: vn(paymentApiJson.surcharge.amount).toNumber(),
              rule: paymentApiJson.surcharge.rule,
            },
        retrieved: true,
      }
    })

    if (saleApiJson.ecom_custom_charges) {
      saleJson.ecomCustomCharges = {
        total: saleApiJson.ecom_custom_charges.total,
        totalIncl: saleApiJson.ecom_custom_charges.total_incl,
        totalTax: saleApiJson.ecom_custom_charges.total_tax,
        charges: map(
          saleApiJson.ecom_custom_charges.charges,
          (ecomCustomCharge: EcomCustomCharge) => {
            return {
              sourceId: ecomCustomCharge.source_id,
              name: ecomCustomCharge.name,
              total: ecomCustomCharge.total,
              totalIncl: ecomCustomCharge.total_incl,
              totalTax: ecomCustomCharge.total_tax,
            }
          }
        ),
      }
    }

    return promiseQueue.then(() => saleJson) as any
  }

  /**
   * Responsible for adding a line item to the array of lineItems for a sale.
   *
   * @param apiLineItem {Object}
   *        A sale line item
   *
   * @param saleJson {Object}
   *        The JSON representation of a sale
   *
   * @return {Promise<Sale>} A promise resolved when the line item has been added to given saleJson
   */
  private addLineItemToSaleJson(
    apiLineItem: ApiLineItem,
    saleJson: SaleJson
  ): IPromise<void> {
    const productId = apiLineItem.product_id

    if (!productId) {
      trackError('[Sale Transformer] Product ID missing from api line item', {
        data: {
          stack: new Error().stack,
        },
      })
    }

    const isTaxInclusive = this.registerManager.isTaxInclusive()

    return this.taxesStore
      .get(apiLineItem.tax_id)
      .then(result => {
        const tax = result
        const lineItemJson: any = {
          id: apiLineItem.id,
          localId: (apiLineItem as any).localId || apiLineItem.id,

          adjusted: Boolean(apiLineItem.price_set),
          isReturn: Boolean(apiLineItem.is_return),
          product_id: productId,
          giftCardNumber: apiLineItem.gift_card_number || '',
          price: apiLineItem.price,
          tax: apiLineItem.tax,
          tax_id: apiLineItem.tax_id,
          loyaltyValue: apiLineItem.loyalty_value,

          explicitLoyaltyValue: Boolean(
            apiLineItem.price_set || apiLineItem.is_return
          ),

          displayDiscount: apiLineItem.discount,

          confirmed: apiLineItem.status === 'CONFIRMED',
          quantity: apiLineItem.quantity,
          note: apiLineItem.note,
          salespersonId: apiLineItem.salesperson_id,

          totals: {
            exclusivePrice: apiLineItem.total_price,
            tax: apiLineItem.total_tax,
          },
        }

        // Add tax components if they are present, only applicable for 2.0 API.
        // Line item tax components are the total tax amounts grouped by rate id,
        // this is the most correct version of tax data
        if (apiLineItem.tax_components) {
          lineItemJson.taxComponents = apiLineItem.tax_components
        }

        if (
          angular.isDefined(apiLineItem.promotions) &&
          apiLineItem.promotions.length
        ) {
          const promotionDetails = apiLineItem.promotions.map(apiPromotion => {
            if (!saleJson.promoCode && apiPromotion.promo_code) {
              saleJson.promoCode = apiPromotion.promo_code
              saleJson.promoCodeStatus = PROMO_CODE_STATUES.APPLIED
            }
            return {
              id: apiPromotion.promotion_id,
              name: apiPromotion.name,
              amount: apiPromotion.amount,
              promoCode: apiPromotion.promo_code,
              promoCodeId: apiPromotion.promo_code_id,
            }
          })

          lineItemJson.promotionDetails = promotionDetails
        }

        // Calculate the basePrice and baseTax for confirmed line items to ensure the availability of discount
        // information.
        const displayPrice = calculateTaxAppropriatePrice(
          apiLineItem.price,
          apiLineItem.tax,
          isTaxInclusive
        )

        let taxRates = tax?.rates || []

        // Detect if there are any custom tax rates (where a different rate is used to the rate associated with the
        // tax ID) used on this line item (only applies to return or confirmed line items). If so, we need to respect
        // these custom tax rates in all our calculations (including the reverse engineering of the base price below).
        // See https://vendhq.atlassian.net/browse/LR-8488 for more details.
        const customTaxRates = calculateCustomTaxRates({
          totalExclusivePrice: apiLineItem.total_price,
          rates: taxRates,
          taxComponents: lineItemJson.taxComponents,
          isReturn: lineItemJson.isReturn,
          isConfirmed: lineItemJson.isConfirmed,
        })
        if (customTaxRates?.length) {
          taxRates = customTaxRates
        }

        const taxRate = calculateTotalTaxRate(taxRates)
        const displayBasePrice = add(displayPrice, apiLineItem.discount)
        const basePriceComponents = calculatePriceComponents(
          displayBasePrice,
          taxRate,
          isTaxInclusive
        )

        lineItemJson.basePrice = basePriceComponents.price
        lineItemJson.baseTax = basePriceComponents.tax
        saleJson.lineItems.push(lineItemJson)
      })
      .catch(error => {
        $log.warn(
          '[SaleTransformer] Failed to retrieve tax information for a confirmed line item',
          error
        )
        return Promise.reject(error)
      })
  }

  /**
   * Hydrate the given JSON representation of a sale to a full Sale model.
   *
   * @param  saleJson {Object}
   *         JSON representation of a sale
   *
   * @return {Promise<Sale>} A promise resolved with the Sale model equivalent of the given JSON
   */
  private async hydrateSale(saleJson: SaleJson) {
    // TODO: this mutates saleJson so we do this first so that other functions like hydrateSaleProducts can operate on
    // the updated saleJson
    await this.hydrateFromGraphQL(saleJson)

    const hydratePaymentsPromise = Promise.all(
      map(saleJson.payments, paymentJson => this.hydratePayment(paymentJson))
    )

    return Promise.all([
      this.hydrateSaleProducts(saleJson),
      hydratePaymentsPromise,
      this.hydrateSaleDiscount(saleJson),
      this.hydrateSaleChange(saleJson),
      this.hydrateCustomer(saleJson),
      this.hydrateUser(saleJson),
      this.hydrateFulfilmentAddressFromGraphQL(saleJson.picklists || []),
    ]).then(
      ([
        lineItems,
        payments,
        saleDiscount,
        saleChange,
        customer,
        user,
        fulfilmentAddress,
      ]) => {
        let displayDiscount: string | number | undefined

        // displayDiscount is required to re-calculate the
        // lineItems.total.discount values in priceManager.totalSale.
        // This field needs to be provided for returns.
        if (saleDiscount && !saleJson.discountIsPercentage) {
          displayDiscount = calculateTaxAppropriatePrice(
            saleDiscount.price,
            saleDiscount.tax,
            this.registerManager.isTaxInclusive()
          )
        }

        const {
          adjustments: adjustmentsJson,
          ecomCustomCharges,
          ...restOfSaleJson
        } = saleJson
        const adjustments = Array.isArray(adjustmentsJson)
          ? adjustmentsJson.map(this.adjustmentConverter)
          : []

        let newFulfilmentDetails = restOfSaleJson.fulfilmentDetails
        if (fulfilmentAddress) {
          newFulfilmentDetails = [
            {
              ...restOfSaleJson.fulfilmentDetails?.[0],
              ...fulfilmentAddress,
            },
          ]
        }

        const sale = new Sale({
          ...restOfSaleJson,
          lineItems,
          payments,
          discount: saleDiscount,
          displayDiscount,
          change: saleChange,
          customer,
          user,
          adjustments,
          ecomCustomCharges,
          fulfilmentDetails: newFulfilmentDetails,
        })

        return sale
      }
    )
  }

  /**
   * Hydrate all products for a sale.
   *
   * @param  saleJson {Object}
   *         JSON representation of a sale
   *
   * @return {Promise<Object>} Resolved once all the products for a sale have been hydrated.
   */
  private hydrateSaleProducts(
    saleJson: Readonly<SaleJson>
  ): IPromise<LineItem[]> {
    let saleHasProductsWithMissingIds = false
    const lineItemsWithMissingId: LineItemJson[] = []

    const hydrateLineItemsPromise = Promise.all(
      map(saleJson.lineItems, lineItemJson => {
        if (!(lineItemJson as any).product_id) {
          saleHasProductsWithMissingIds = true
          lineItemsWithMissingId.push(lineItemJson)
        }
        return this.hydrateLineItem(lineItemJson)
      })
    )

    if (saleHasProductsWithMissingIds) {
      const { saleDate, status, syncStatus } = saleJson

      trackError('[hydrateSaleProducts] Sale has products with missing ids', {
        data: {
          stack: new Error().stack,
          lineItemsWithMissingId,
          saleDate,
          status,
          syncStatus,
        },
      })
    }

    return hydrateLineItemsPromise
  }

  /**
   * Hydrate the customer associated with a sale from the customer ID stored with the sale JSON.
   *
   * @param  saleJson {Object}
   *         JSON representation of a sale
   *
   * @return {Promise<Object>} A promise resolved with the updated JSON representation of a sale including
   *         the full Customer object
   */
  private hydrateCustomer(
    saleJson: Readonly<SaleJson>
  ): IPromise<ApiCustomer | undefined> {
    if (saleJson.customer_id) {
      return this.customersStore.get(saleJson.customer_id)
    } else {
      return Promise.resolve(undefined)
    }
  }

  /**
   * Hydrate the user associated with a sale from the user ID stored with the sale JSON.
   *
   * @param  saleJson {Object}
   *         JSON representation of a sale
   *
   * @return {Promise<Object>} A promise resolved with the updated JSON representation of a sale including
   *         the full User object
   */
  private hydrateUser(saleJson: SaleJson): IPromise<User | undefined> {
    if (saleJson.user_id) {
      return this.usersStore.get(saleJson.user_id).then(user => {
        return user ? apiUserToSaleUser(user) : Promise.resolve(undefined)
      })
    } else {
      return Promise.resolve(undefined)
    }
  }

  /**
   * Hydrate the JSON representation of a LineItem to the full model.
   *
   * @param  lineItemJson {Object}
   *         JSON representation of a LineItem
   *
   * @return {Promise<LineItem>} A promise resolved with the full LineItem model
   */
  private hydrateLineItem(
    lineItemJson: Readonly<LineItemJson>
  ): IPromise<LineItem> {
    const promises = [
      this.hydrateProduct((lineItemJson as any).product_id), // product
      this.taxesStore.get((lineItemJson as any).tax_id), // taxInfo
      this.hydratePromotion(lineItemJson.promotion), // promotion
    ]
    let salesperson: LineItemUser | null

    if (this.vdFeatureManager.get('sales_person_attribution')) {
      const userId =
        lineItemJson.salesperson?.id || (lineItemJson as any).salespersonId
      salesperson = this.hydrateLineItemUser(userId)
    }

    return Promise.all(promises).then(([product, taxInfo, promotion]) => {
      if (promotion && promotion instanceof Promotion) {
        // LineItemJson.promotion is deprecated, so we extract
        // the promotion details and line item promotion from it
        lineItemJson = {
          ...lineItemJson,
          promotionDetails: promotion.promotionDetails,
          lineItemPromotion: promotion.lineItemPromotion,
        }
      }

      merge(lineItemJson, { product, taxInfo, salesperson })
      return createLineItem(lineItemJson as any)
    })
  }

  /**
   * Hydrate the line item user data based on the user id
   * This will return the current logged in user if the user/id doesnt exist
   *
   * @param userId
   *
   * @returns {LineItemUser}
   */
  private hydrateLineItemUser(userId?: string): LineItemUser | null {
    if (!userId) {
      return null
    }

    const user = this.registerManager.getUserById(userId)
    if (user) {
      return {
        id: user.id,
        username: user.username,
        displayName: user.display_name,
        email: user.email,
      } as LineItemUser
    } else {
      // Sending the userId that cant be found to investigate if this happen
      trackError(
        '[SaleTransformer] Unable to set the salesperson on line item',
        { data: { userId } }
      )
      return null
    }
  }

  /**
   * Hydrate the JSON representation of a Promotion to the full model.
   *
   * @param  promotionJson {Object}
   *         JSON representation of a promotion
   *
   * @return {Promise<Promotion>} A promise resolved with the full Promotion model
   */
  private hydratePromotion(
    promotionJson: any
  ): Promotion | IPromise<Promotion> | null {
    if (!promotionJson) {
      return null
    }

    // Due to a model property change from blazePromotion to lineItemPromotion we just do a quick check and rename
    // if the blazePromotion property is found. Should be able to remove this after a while.
    if (promotionJson.blazePromotion) {
      promotionJson.lineItemPromotion = promotionJson.blazePromotion
    }

    return new Promotion({
      lineItemPromotion: promotionJson.lineItemPromotion,
      promotionDetails: promotionJson.promotionDetails,
    })
  }

  /**
   * Hydrate the JSON representation of a Payment to the full model.
   *
   * @param  paymentJson {Object}
   *         JSON representation of a Payment
   *
   * @return {Promise<Payment>} A promise resolved with the full Payment model
   */
  private hydratePayment(paymentJson: PaymentJson): IPromise<Payment> {
    return paymentTypesStore
      .get(paymentJson.payment_type_id)
      .then(paymentType => ({
        ...omit(paymentJson, 'payment_type_id'),
        paymentType: paymentType!,
      }))
  }

  /**
   * Hydrate the given JSON representation of a sale with data from GraphQL.
   *
   * @param  saleJson {Object}
   *         JSON representation of a sale.
   *
   * @return {Promise<void>}
   */
  // TODO: This function mutates saleJson, which is not ideal
  private hydrateFromGraphQL(saleJson: SaleJson): IPromise<void> {
    const saleInput = (saleJson as unknown) as SaleInput
    if (
      saleInput.id === undefined ||
      saleInput.hydratedFromGraphQL ||
      !this.webRegisterStatusManager.isOnline()
    ) {
      return Promise.resolve()
    }

    return client
      .query<
        {
          sale: GraphQLSale
          saleCustomFields: GraphQLCustomField[]
          lineItemCustomFields: GraphQLCustomField[]
        },
        { id: string }
      >({
        query: SaleQuery,
        variables: {
          id: saleInput.id,
        },
      })
      .then(gqlResult => {
        if (
          !gqlResult.data?.sale ||
          !gqlResult.data?.saleCustomFields ||
          !gqlResult.data?.lineItemCustomFields
        ) {
          return
        }

        const saleCustomFields = keyBy(
          gqlResult.data.saleCustomFields,
          cf => cf.name
        )

        const lineItemCustomFields = keyBy(
          gqlResult.data.lineItemCustomFields,
          cf => cf.name
        )

        const formatCustomFields = (
          values: GraphQLCustomFieldValue[] | undefined,
          customFields: Record<string, GraphQLCustomField>
        ): CustomFieldValue[] => {
          const output: CustomFieldValue[] = []
          if (!values) {
            return output
          }

          values.forEach(cf => {
            const field = customFields[cf.name]
            if (!field) {
              return
            }

            const value: CustomFieldValue = {
              name: field.name,
              title: field.title,
              definitionId: field.id,
              visibleInUI: field.visibleInUI,
              printOnReceipt: field.printOnReceipt,
              type: CustomFieldTypeFromGraphQL[field.type],
              value: null,
            }

            switch (field.type) {
              case CustomFieldType.STRING:
              case CustomFieldType.DATE:
              case CustomFieldType.QRCODE:
                value.value = cf.stringValue ?? null
                break
              case CustomFieldType.INTEGER:
                value.value = cf.integerValue ?? null
                break
              case CustomFieldType.BOOLEAN:
                value.value = cf.booleanValue ?? null
                break
              case CustomFieldType.PRODUCT:
                value.value = cf.product?.id ?? null
                break
              case CustomFieldType.STRING_ARRAY:
                value.value = cf.stringArrayValue ?? null
                break
            }

            output.push(value)
          })

          return output
        }

        saleInput.customFields = formatCustomFields(
          gqlResult.data.sale.customFields,
          saleCustomFields
        )

        saleInput.lineItems?.forEach(lineItem => {
          const values = gqlResult.data.sale.lineItems.find(
            li => li.id === lineItem.id
          )?.customFields

          lineItem.customFields = formatCustomFields(
            values,
            lineItemCustomFields
          )
        })

        if (this.vdFeatureManager.get('fulfillment_lif')) {
          // Reduce to Map<string, [FulfilmentType, boolean, string]>
          // { SaleLineItemId: [FulfilmentType, FulfilmentCompleted, FulfilmentNote] }
          const lineItemFulfilmentTypeMap = getItemFulfilmentMap(
            gqlResult.data.sale
          )

          if (lineItemFulfilmentTypeMap.size > 0) {
            // We get the first fulfilment detail from the map as currently
            // one fulfilment detail per sale is supported
            const [type, , note] =
              lineItemFulfilmentTypeMap.values().next().value || []
            saleInput.fulfilmentDetails = [{ type, note }] as FulfilmentDetail[]

            saleInput.lineItems?.forEach(li => {
              if (li.id && lineItemFulfilmentTypeMap.has(li.id)) {
                const [type, isCompleted] = lineItemFulfilmentTypeMap.get(
                  li.id
                )!
                li.fulfilmentType = type
                // A fulfilment is completed if it is already dispatched or picked up
                li.fulfilmentCompleted = isCompleted
              }
            })
          }
        }

        if (getFeatureFromCache(FEATURE_CAPTURE_ADDRESS_SELL_SCREEN)) {
          saleInput.picklists = this.gqlPicklistsToSalePicklists(
            gqlResult.data.sale.picklists
          )
        }

        saleInput.hydratedFromGraphQL = true

        if (!saleInput.isSavedService && saleInput.customFields?.length) {
          saleInput.isSavedService = saleInput.customFields.some(
            (field: CustomFieldValue) =>
              field.name === SERVICE_ORDERS_CUSTOM_FIELD && field.value
          )
        }
      })
  }

  private hydrateFulfilmentAddressFromGraphQL(
    picklists: SalePicklist[]
  ): IPromise<void | Partial<FulfilmentDetail>> {
    if (!getFeatureFromCache(FEATURE_CAPTURE_ADDRESS_SELL_SCREEN)) {
      return Promise.resolve()
    }

    const deliveryPicklistId = (picklists || []).find(
      picklist => picklist.type === FulfilmentType.DELIVERY
    )?.id

    if (!deliveryPicklistId) {
      return Promise.resolve()
    }

    return client
      .query<
        {
          picklist: GraphQLPicklist
        },
        { id: string }
      >({
        query: PicklistQuery,
        variables: {
          id: deliveryPicklistId,
        },
      })
      .then(picklistResult => {
        return {
          billingAddress: this.gqlFulfilmentAddressToAddressDetails(
            picklistResult.data.picklist.billingAddress
          ),
          shippingAddress: this.gqlFulfilmentAddressToAddressDetails(
            picklistResult.data.picklist.shippingAddress
          ),
        }
      })
  }

  private gqlFulfilmentAddressToAddressDetails(
    gqlAddress: GraphQLPartialFulfillmentAddress | undefined
  ): AddressDetails {
    return {
      address_1: gqlAddress?.addressLine1 || '',
      address_2: gqlAddress?.addressLine2 || '',
      suburb: gqlAddress?.suburb || '',
      city: gqlAddress?.city || '',
      state: gqlAddress?.stateCode || '',
      postcode: gqlAddress?.postcode || '',
      country_id: gqlAddress?.countryCode || '',
    }
  }

  private gqlPicklistsToSalePicklists(
    gqlPicklists: GraphQLPicklist[]
  ): SalePicklist[] {
    if (!gqlPicklists) {
      return []
    }

    return gqlPicklists.map(picklist => {
      return {
        id: picklist.id,
        type: (picklist.type as unknown) as FulfilmentType,
      }
    })
  }

  /**
   * Hydrate the JSON representation of a SaleDiscount to the full model and link it up to the associated
   * JSON representation of the Sale.
   *
   * @param  saleJson {Object}
   *         JSON representation of a Sale that has a sale discount
   *
   * @return {Promise} A promise resolved with the updated JSON representation of a Sale with the associated
   *         full SaleDiscount model attached
   */
  private hydrateSaleDiscount(
    saleJson: Readonly<SaleJson>
  ): IPromise<SaleDiscount | null> {
    const discountJson = saleJson.discount

    if (!discountJson) {
      return Promise.resolve(null)
    }

    return this.taxesStore.get((discountJson as any).tax_id).then(taxInfo => {
      return createSaleDiscount({
        ...discountJson,
        taxInfo,
        taxComponents: discountJson.taxComponents as ApiLineItemTaxComponent[], // FIXME: we should stop using this
      })
    })
  }

  /**
   * Hydrate the JSON representation of the sale change Payment to the full Payment model.
   *
   * @param  saleJson
   *         JSON representation of a Sale that has a sale discount
   *
   * @return {Promise} A promise resolved with the updated JSON representation of a Sale with the associated
   *         full change Payment model attached
   */
  private hydrateSaleChange(
    saleJson: Readonly<SaleJson>
  ): IPromise<Payment | null> {
    return saleJson.change
      ? this.hydratePayment(saleJson.change)
      : Promise.resolve(null)
  }

  /**
   * Format the given sale as the /api/quote end point expects it.
   *
   * @param  sale {Sale}
   *         The sale to transform to the 0.x API format
   *
   * @return {Object} JSON representation of the quote formatted as the API expects it
   */
  public toQuoteApi(sale: Sale): QuoteData {
    const formattedQuote = {
      status: QuoteStatus.CREATED,
      quoteDate: sale.saleDate,
      customerId: sale.customer && sale.customer.id,
      registerId: sale.registerId || '',
      totalTax: sale.finals.tax,
      totalPrice: sale.finals.exclusivePrice,
      shortCode: sale.shortCode || '',
      quoteProducts: map(sale.lineItems, (lineItem, i) =>
        this.formatProductForQuote(lineItem, i)
      ),
      note: sale.note,
      retailerId: '', // Waiting for API to make this optional
      outletId: this.registerManager.getActiveOutlet().id || '',
      userId: sale.user?.id || '',
      loyalty: parseFloat(
        this.retailerSettingsManager.getLoyaltyRatio().toString()
      ).toFixed(5),
    }
    if (sale.hasDiscount()) {
      formattedQuote.quoteProducts.push(
        this.createDiscountLineItemForQuoteApi(sale)
      )
    }

    return formattedQuote
  }

  /**
   * Convert Quote to Sale
   *
   * @param  quote {QuoteData}
   *         The quote object received from API
   *
   * @return {Sale}  Sale
   */
  public toSaleFromQuote(quote: QuoteDataWithExtras): IPromise<Sale> {
    const {
      id,
      customer,
      userId,
      quoteProducts,
      totalTax,
      totalPrice,
      note,
      loyalty,
      shortCode,
    } = quote

    const saleJson: any = {
      id: uuid(),
      customer_id: customer.id,
      register_id: this.posStateManager.getRegisterId(), // Use the current register id so "Register Closure Transaction" will record the transactions for the current register
      outlet_id: this.posStateManager.getOutletId(), // Use the current outlet id so "Register Closure Transaction" will record the transactions for the current outlet
      user_id: userId,
      source: SaleSource.QUOTE,
      source_id: id, // quote ID
      note,
      short_code: shortCode,
      return_for: null,
      total_price: vn(totalPrice).toNumber(),
      total_tax: vn(totalTax).toNumber(),
      total_loyalty: 0,
      created_at: '',
      updated_at: '',
      sale_date: '',
      deleted_at: null,
      line_items: quoteProducts.map(item => {
        return {
          id: item.id || '',
          product_id: item.productId,
          price_set: true,
          price: vn(item.price).toNumber(),
          discount: vn(item.discount).toNumber(),
          quantity: vn(item.quantity).toNumber(),
          tax_id: item.taxId,
          tax: vn(item.tax).toNumber(),
          tax_components: item.taxComponents,
          totals: {
            tax: vn(item.tax).toNumber(),
            inclusivePrice: vn(item.price).toNumber(),
          },
          note: item.note,
          salesperson_id: item.salesperson?.id,
        }
      }),
      version: 0,
      receipt_number: '',
      total_price_incl: vn(totalPrice).toNumber(),
      payments: [],
      taxes: [],
      invoice_number: '',
      loyalty,
    }

    return this.toModel(saleJson).then(sale => {
      // Add required fields for new created sale
      // eslint-disable-next-line array-callback-return
      sale.lineItems.map(lineItem => {
        Object.assign(lineItem, {
          registerId: this.registerManager.getActiveRegister().id,
          registerSequenceId: this.registerManager.getActiveRegister()
            .register_open_sequence_id,
        })
      })

      return sale
    })
  }

  /**
   * Format the given tax component array as /api/quotes end point expects it
   *
   * @param taxComponent {ApiLineItemTaxComponent}
   *
   * @return {TaxComponent}
   */

  private formatTaxComponentForQuotes(
    taxComponent: ApiLineItemTaxComponent
  ): TaxComponent {
    return {
      rateId: taxComponent.rate_id,
      totalTax: parseFloat(taxComponent.total_tax.toString()).toFixed(5),
    }
  }

  /**
   * Format the given line item as the /api/quotes end point expects it.
   *
   * @param  lineItem {LineItem}
   *         A line item to transform to the 0.x API format
   *
   * @param  index {Number}
   *         The index of the line item within the sale
   *
   * @return {Object} JSON representation of the line item formatted as the API expects it
   */

  private formatProductForQuote(lineItem: LineItem, index: number): any {
    const tax = vn(lineItem.quantity).isZero()
      ? 0
      : divide(vn(lineItem.totals.tax), vn(lineItem.quantity))
    const apiLineItem = {
      productId: this.getLineItemProductId(lineItem),

      // Price is always exclusive
      price: round(lineItem.price, 5),

      // @note depending on config discount is posted as inclusive or exclusive (i.e. display value)
      // @todo this sucks. The API should accept an exclusive value same as price
      discount: lineItem.displayDiscount?.toString() || '',

      tax: round(tax, 5),
      taxId: lineItem.taxInfo!.id,
      quantity: lineItem.quantity.toString(),
      sequence: index,
      taxComponents: lineItem.taxComponents.map(component =>
        this.formatTaxComponentForQuotes(component)
      ),
      note: lineItem.note,
      salespersonId: lineItem.salesperson?.id,
    }

    return apiLineItem
  }

  /**
   * Create the discount line item containing all the info in the SaleDiscount model to send to the server.
   *
   * @param  {Sale} sale
   *         The sale to create the discount line item for
   *
   * @return {Object} JSON representation of the discount line item (register sale product) formatted as the
   *         API expects it
   */

  private createDiscountLineItemForQuoteApi(sale: Sale): unknown {
    const discount = sale.discount!
    let saleDiscountTaxComponents = []
    const saleDiscountTaxComponentsMap: {
      [key: string]: any
    } = {}
    sale.lineItems.forEach(lineItem => {
      if (!vn(lineItem.totals.saleDiscountTax).eq(0)) {
        // create the map element if necessary
        lineItem.taxComponents.forEach(taxComponent => {
          saleDiscountTaxComponentsMap[
            taxComponent.rate_id
          ] = saleDiscountTaxComponentsMap[taxComponent.rate_id] || {
            total_tax: 0,
            rate_id: taxComponent.rate_id,
          }

          // add any previous intermediate result
          saleDiscountTaxComponentsMap[taxComponent.rate_id].total_tax = add(
            saleDiscountTaxComponentsMap[taxComponent.rate_id].total_tax,
            subtract(
              taxComponent.total_tax,
              (taxComponent as any).total_tax_after_sale_discount
            )
          )
        })
      }
    })

    saleDiscountTaxComponents = Object.keys(saleDiscountTaxComponentsMap).map(
      rateId => ({
        rateId,
        totalTax: String(-saleDiscountTaxComponentsMap[rateId].total_tax),
      })
    )

    return {
      productId: this.retailerSettingsManager.get().discount_product_id,

      price: discount.price.toString(),
      tax: discount.tax.toString(),
      taxId: discount.taxInfo!.id,
      discount: '0',
      quantity: '-1',
      sequence: sale.lineItems.length,
      taxComponents: saleDiscountTaxComponents.length
        ? saleDiscountTaxComponents
        : undefined,
    }
  }

  /**
   * Check if sale object has originated from a quote
   *
   * @param {Sale | ApiSale} sale
   * @returns {boolean}
   */
  private isSourcedFromQuote(
    sale: Pick<Sale | ApiSale | ReduxCurrentSale, 'source'>
  ): boolean {
    return sale.source === SaleSource.QUOTE
  }

  /**
   * `status` in new create sale is made up of type, state, paymentState
   * and fulfilmentState. This function will return the new status based on the
   * old sale status
   *
   * @param status SaleStatus
   */
  public saleStatusToStateConverter(status: SaleStatus): NewSaleStatus {
    let type: SaleType = SaleType.REGULAR
    let state: SaleState = SaleState.CLOSED
    let paymentState: SalePaymentState = SalePaymentState.COMPLETE
    let fulfilmentState: SaleFulfilmentState | null = null

    switch (status) {
      case SaleStatus.ACCOUNT_SALE:
        type = SaleType.ONACCOUNT
        state = SaleState.CONFIRMED
        paymentState = SalePaymentState.PENDING
        break

      case SaleStatus.ACCOUNT_SALE_CLOSED:
        type = SaleType.ONACCOUNT
        break

      case SaleStatus.AWAITING_DISPATCH:
      case SaleStatus.AWAITING_PICKUP:
        fulfilmentState = SaleFulfilmentState.PENDING
        break

      case SaleStatus.DISPATCHED_CLOSED:
      case SaleStatus.PICKED_UP_CLOSED:
        fulfilmentState = SaleFulfilmentState.COMPLETE
        break

      case SaleStatus.LAYBY_SALE:
        type = SaleType.LAYBY
        state = SaleState.CONFIRMED
        paymentState = SalePaymentState.PENDING
        break

      case SaleStatus.LAYBY_SALE_CLOSED:
        type = SaleType.LAYBY
        break

      case SaleStatus.PARKED:
      case SaleStatus.QUOTE:
        state = SaleState.PARKED
        paymentState = SalePaymentState.PENDING
        break

      case SaleStatus.VOIDED:
        state = SaleState.VOIDED
        break

      default:
        break
    }

    return { type, state, paymentState, fulfilmentState }
  }

  /**
   * Convert an adjustment of type ApiSaleAdjustment to type SaleAdjustment
   * @param adjustment
   */
  private adjustmentConverter(adjustment: ApiSaleAdjustment): SaleAdjustment {
    const { adjustment_type, ...rest } = adjustment
    return {
      ...rest,
      type: adjustment_type,
    }
  }

  /**
   * Determines if this sale has a service order with details for items to be
   * serviced.
   * @returns {boolean} True if there are any item details, false otherwise.
   */
  private hasServiceOrderWithItemDetails(
    sale: Pick<Sale | ReduxCurrentSale, 'serviceFields' | 'customFields'>
  ) {
    const { serviceFields, customFields } = sale

    if (
      !hasServiceOrder(serviceFields, customFields) ||
      (!serviceFields?.serviceItem?.itemDetails?.itemName &&
        !serviceFields?.serviceItem?.location)
    ) {
      return false
    }
    return true
  }

  private createServiceSaleDiscount(
    sale: Pick<
      Sale | ReduxCurrentSale,
      'discount' | 'discountIsPercentage' | 'finals'
    >
  ) {
    if (!sale.discount) {
      return null
    }

    const isTaxInclusive = this.registerManager.isTaxInclusive()
    const saleDiscount = isTaxInclusive
      ? add(sale.discount.price, sale.discount.tax)
      : vn(sale.discount.price).toNumber()

    let percentDiscount = 0
    if (sale.discountIsPercentage) {
      const totalPrice = add(
        sale.finals.exclusivePrice,
        isTaxInclusive ? sale.finals.tax : 0
      )

      percentDiscount = divide(saleDiscount, add(saleDiscount, totalPrice))
    }

    const discountValue = sale.discountIsPercentage
      ? percentDiscount
      : saleDiscount

    return {
      isPercentage: sale.discountIsPercentage,
      value: round(discountValue, 5),
    }
  }
}

angular
  .module('webregister.factories')
  .factory('saleTransformer', SaleTransformer)
